import { createBranch, getLanguage } from "../Managers/dataProvider";
import {
  BranchModel,
  EnumAlertDialogType,
  EnumPopupFunctions,
  PopupInfoModel,
} from "../Managers/dataStructures";
import React, { useState } from "react";
import {
  postBranchOnServer,
  timeout,
  updateBranchInfo,
} from "../Managers/wepApiManager";
import { ActivityIndicator, Toast } from "../Managers/utilityComponents";
import { checkEntity, isValiEmail } from "../Managers/utilityFunctions";

interface BranchDetailProps {
  ftype: EnumPopupFunctions;
  branch?: BranchModel;
  closePopup: (item: PopupInfoModel) => void;
  popupItem: PopupInfoModel;
}

export default function BranchDetail(props: BranchDetailProps) {
  const isEnglish = getLanguage();
  let [branchEle] = React.useState<BranchModel>(
    props.branch !== undefined ? props.branch : createBranch()
  );
  const [issActivityIndicator, setIsActivityIndicator] =
    useState<boolean>(false);
  const [toastText, setToastText] = useState<string>("");
  const [isToastVisible, setIsToastVisible] = useState<EnumAlertDialogType>(
    EnumAlertDialogType.NEUTRAL
  );
  const isDisabled: boolean = issActivityIndicator;

  function onSaveData() {
    if (checkBranchInfo() === false) {
      return;
    }

    if (props.ftype === EnumPopupFunctions.ADD_BRANCH) {
      setIsActivityIndicator(true);
      postBranchOnServer(branchEle, (isSuccess: boolean, branch_id: string) => {
        setIsActivityIndicator(false);
        if (isSuccess) {
          showAlert(
            true,
            isEnglish
              ? "Success: New Branch created."
              : "Erfolg: Neue Filiale erstellt.",
            () => {
              const item_: PopupInfoModel = {
                item: props.branch,
                ftype: EnumPopupFunctions.CLOSE,
              };
              branchEle.branch_id = branch_id;
              if (props.popupItem !== undefined) {
                if (props.popupItem.callback !== undefined) {
                  props.popupItem.callback(branchEle);
                }
              }
              props.closePopup(item_);
            }
          );
        } else {
          showAlert(
            false,
            isEnglish
              ? "Error: Unable to create branch."
              : "Fehler: Filiale konnte nicht erstellt werden.",
            () => {}
          );
        }
      });
    } else if (props.ftype === EnumPopupFunctions.BRANCH_DETAIL) {
      setIsActivityIndicator(true);
      updateBranchInfo(branchEle, (isSuccess: boolean) => {
        setIsActivityIndicator(false);
        if (isSuccess) {
          showAlert(
            true,
            isEnglish
              ? "Success: Branch information has been updated."
              : "Erfolg: Filialinformationen wurden aktualisiert.",
            () => {
              const item_: PopupInfoModel = {
                item: props.branch,
                ftype: EnumPopupFunctions.CLOSE,
              };
              props.closePopup(item_);
            }
          );
        } else {
          showAlert(
            false,
            isEnglish
              ? "Error: Unable to update branch data."
              : "Fehler: Filialdaten konnten nicht aktualisiert werden.",
            () => {}
          );
        }
      });
    }
  }

  function checkBranchInfo() {
    if (checkEntity(branchEle.name) === false) {
      showAlert(
        false,
        isEnglish ? "Invalid branch name." : "Ungültiger Filialname.",
        () => {}
      );
      return false;
    }
    if (checkEntity(branchEle.phone) === false) {
      showAlert(
        false,
        isEnglish
          ? "Invalid branch phone number."
          : "Ungültige Filialtelefonnummer.",
        () => {}
      );
      return false;
    }
    if (checkEntity(branchEle.contact_name) === false) {
      showAlert(
        false,
        isEnglish ? "Invalid contact name." : "Ungültiger Ansprechpartner.",
        () => {}
      );
      return false;
    }
    if (checkEntity(branchEle.contact_phone) === false) {
      showAlert(
        false,
        isEnglish
          ? "Invalid contact phone number."
          : "Ungültige Telefonnummer des Ansprechpartners.",
        () => {}
      );
      return false;
    }
    if (checkEntity(branchEle.contact_email) === false) {
      showAlert(
        false,
        isEnglish
          ? "Invalid contact email."
          : "Ungültige E-Mail-Adresse des Ansprechpartners.",
        () => {}
      );
      return false;
    } else if (isValiEmail(branchEle.contact_email) === false) {
      showAlert(
        false,
        isEnglish
          ? "Invalid contact email."
          : "Ungültige E-Mail-Adresse des Ansprechpartners.",
        () => {}
      );
      return false;
    }
    if (checkEntity(branchEle.address) === false) {
      showAlert(
        false,
        isEnglish ? "Invalid branch address." : "Ungültige Filialadresse.",
        () => {}
      );
      return false;
    }

    return true;
  }

  function onCancel() {
    const item_: PopupInfoModel = {
      item: null,
      ftype: EnumPopupFunctions.CLOSE,
    };
    props.closePopup(item_);
  }

  async function showAlert(
    isPositive: boolean,
    text: string,
    callback: () => void
  ) {
    setToastText(text);
    if (isPositive) {
      setIsToastVisible(EnumAlertDialogType.POSITIVE);
    } else {
      setIsToastVisible(EnumAlertDialogType.NEGATIVE);
    }
    await timeout(2000);
    setIsToastVisible(EnumAlertDialogType.NEUTRAL);
    callback();
  }

  return (
    <div className="relative isolate bg-white">
      <div className="flex justify-center max-w-7xl">
        <div className="px-6 pb-24 pt-20">
          <div className="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="branch-name"
                  className="block text-sm font-semibold leading-6 text-gray-900 text-nowrap overflow-clip"
                >
                  {isEnglish ? "Branch Name" : "Filiale"}
                </label>
                <div className="mt-2.5">
                  <input
                    type="text"
                    name="branch-name"
                    id="branch-name"
                    autoComplete="given-name"
                    disabled={isDisabled}
                    defaultValue={branchEle.name}
                    onChange={(x) => (branchEle.name = x.currentTarget.value)}
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="phone-number"
                  className="block text-sm font-semibold leading-6 text-gray-900 text-nowrap overflow-clip"
                >
                  {isEnglish ? "Phone Number" : "Telefonnummer"}
                </label>
                <div className="mt-2.5">
                  <input
                    type="string"
                    name="phone-number"
                    id="phone-number"
                    autoComplete="family-name"
                    disabled={isDisabled}
                    defaultValue={branchEle.phone}
                    onChange={(x) => (branchEle.phone = x.currentTarget.value)}
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="contact-name"
                  className="block text-sm font-semibold leading-6 text-gray-900 text-nowrap overflow-clip"
                >
                  {isEnglish ? "Contact Name" : "Filialleiter"}
                </label>
                <div className="mt-2.5">
                  <input
                    type="text"
                    name="contact-name"
                    id="contact-name"
                    autoComplete="given-name"
                    disabled={isDisabled}
                    defaultValue={branchEle.contact_name}
                    onChange={(x) =>
                      (branchEle.contact_name = x.currentTarget.value)
                    }
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="">
                <label
                  htmlFor="contact-phone-name"
                  className="block text-sm font-semibold leading-6 text-gray-900 text-nowrap overflow-clip"
                >
                  {isEnglish ? "Contact Phone Number" : "Telefonnummer"}
                </label>
                <div className="mt-2.5">
                  <input
                    type="string"
                    name="contact-phone-number"
                    id="contact-phone-number"
                    autoComplete="family-name"
                    disabled={isDisabled}
                    defaultValue={branchEle.contact_phone}
                    onChange={(x) =>
                      (branchEle.contact_phone = x.currentTarget.value)
                    }
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="contact-emailr="
                  className="block text-sm font-semibold leading-6 text-gray-900 text-nowrap overflow-clip"
                >
                  {isEnglish ? "Contact Email" : "E-Mail-Adresse"}
                </label>
                <div className="mt-2.5">
                  <input
                    type="email"
                    name="contact-email"
                    id="contact-email"
                    autoComplete="email"
                    disabled={isDisabled}
                    defaultValue={branchEle.contact_email}
                    onChange={(x) =>
                      (branchEle.contact_email = x.currentTarget.value)
                    }
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="message"
                  className="block text-sm font-semibold leading-6 text-gray-900 text-nowrap overflow-clip"
                >
                  {isEnglish ? "Branch Address" : "Filialadresse"}
                </label>
                <div className="mt-2.5">
                  <textarea
                    name="address"
                    id="address"
                    rows={3}
                    disabled={isDisabled}
                    defaultValue={branchEle.address}
                    onChange={(x) =>
                      (branchEle.address = x.currentTarget.value)
                    }
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  ></textarea>
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              {issActivityIndicator === true ? ActivityIndicator(1) : null}
              <button
                onClick={onCancel}
                type="button"
                className="text-sm font-semibold leading-6 text-gray-900 mx-5"
              >
                {isEnglish ? "Close" : "Schließen"}
              </button>
              <button
                onClick={onSaveData}
                disabled={isDisabled}
                className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                {isEnglish ? "Save" : "Speichern"}
              </button>
            </div>
            <div className="mt-4">
              {isToastVisible === EnumAlertDialogType.POSITIVE ||
              isToastVisible === EnumAlertDialogType.NEGATIVE
                ? Toast(toastText, isToastVisible)
                : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
